/**
 * `/hjalp` "Så fungerar sidan" (slice C, P1-8): the words the site uses, how to write a numeric
 * answer, the keyboard shortcuts and a short English glossary for exchange students (ruling 1:
 * the UI stays Swedish; this one section is in English and marked `lang="en"`).
 *
 * The text is Swedish only, like the rest of the UI (2026-09-28). Keep it in step with the code
 * it describes: number formats `shared/grading.ts` (parseNumericAnswer), keys
 * `features/practice/SessionPage.tsx`, `ConfidenceControl.tsx` and `features/flashcards/`.
 * Physics terms follow planning/terminology/TERM_FINAL.md.
 */
import { Link } from "react-router";
import { Card, Kbd, PageHeader } from "@client/components/ui";
import { REOPEN_HREF } from "./onboarding/KomIgangCard";

const LINK = "text-accent-700 underline underline-offset-2 hover:no-underline dark:text-accent-400";

const WORDS: readonly [string, string][] = [
  [
    "Moment",
    "Kursens stora ämnesblock, till exempel relativitet, kvantfysik eller kärnfysik. Ett moment hör till några föreläsningar.",
  ],
  [
    "F01–F12, R01–R03",
    "Kursens föreläsningar (F) och räknestugor (R). Kortet överst på startsidan visar nästa och vad du kan förbereda.",
  ],
  [
    "Dagens pass",
    "En kort blandning byggd åt dig: kort att repetera, uppgifter du missade tidigare och något nytt från föreläsningen som är på gång.",
  ],
  ["Snabbkoll", "En kort fråga, flerval eller en uträkning, på ungefär 2 minuter."],
  [
    "Uppgift (som på tentan)",
    "En längre uppgift. Du skriver din lösning, visar lösningen och bockar själv av bedömningspunkterna.",
  ],
  [
    "Säkerhet 1–4",
    "Hur säker du är på ditt svar: Gissar, Osäker, Ganska säker, Helt säker. Den styr när uppgiften kommer tillbaka och kostar aldrig poäng.",
  ],
  [
    "Principsteget",
    "Innan vissa uppgifter väljer du vilken princip du ska använda. Det kostar aldrig poäng, men det tränar det tentan frågar efter.",
  ],
  [
    "Vilken princip missade du?",
    "Efter en självrättad uppgift under full poäng. Välj ett snabbsvar, till exempel Räknefel eller Vet inte än, eller skriv med egna ord.",
  ],
  [
    "Repetition",
    "Uppgifter du missade kommer tillbaka några dagar senare, som en ny variant eller en annan uppgift i samma ämne.",
  ],
  [
    "Kort",
    "Begreppskort. Tänk ut svaret, skriv gärna några ord, välj hur säker du är och vänd kortet.",
  ],
  [
    "Underlag",
    "Under Framsteg: hur mycket du har övat i ett moment. Ett tunt underlag betyder att siffran bygger på få svar än.",
  ],
  ["Övningstenta", "En gammal tenta som du skriver på tid, med formelsamlingen som på riktigt."],
];

const ANSWER_EXAMPLES: readonly [string, string][] = [
  ["2,5 eller 2.5", "Decimalkomma eller decimalpunkt, men inte båda i samma tal."],
  ["6,63e-34", "Tiopotens med e."],
  [
    "6,63·10^-34 eller 6,63 x 10^-34",
    "Tiopotens med 10^. Måsvingar runt exponenten går också bra.",
  ],
  ["−1,5", "Minustecken, även det långa från telefonens tangentbord."],
  ["1 200 000", "Mellanslag mellan siffergrupper."],
  [
    "13,6 eV",
    "Enheten står redan bredvid rutan, så du behöver inte skriva den. Skriver du en, ska den passa uppgiften.",
  ],
];

const KEYS: readonly { id: string; keys: string[]; text: string }[] = [
  {
    id: "enter",
    keys: ["Enter"],
    text: "Svara, eller gå vidare från svarsrutan till säkerheten om den saknas.",
  },
  {
    id: "rating",
    keys: ["1", "2", "3", "4"],
    text: "Välj säkerhet när fokus står i raden Hur säker är du?, och tryck sedan Enter för att svara.",
  },
  { id: "hint", keys: ["H"], text: "Visa nästa ledtråd." },
  { id: "next", keys: ["N"], text: "Nästa fråga, när du har fått svar." },
  {
    id: "done",
    keys: ["Ctrl", "Enter"],
    text: "Klar i en längre uppgift, från textrutan (Cmd + Enter på Mac).",
  },
  {
    id: "space",
    keys: ["Mellanslag"],
    text: "Trycker på knappen som har fokus, till exempel en betygsknapp på ett kort.",
  },
  {
    id: "card-rate",
    keys: ["1", "2", "3"],
    text: "Kort: välj hur säker du är (Osäker, Ganska säker, Helt säker) och vänd kortet.",
  },
  {
    id: "card-grade",
    keys: ["1", "2", "3", "4"],
    text: "Kort, efter vändningen: Igen, Svårt, Bra, Lätt.",
  },
  { id: "esc", keys: ["Esc"], text: "Stänger menyn, och lämnar svarsrutan på ett kort." },
];

const ENGLISH: readonly [string, string][] = [
  ["moment", "module (a block of the course)"],
  ["föreläsning, räknestuga", "lecture, problem session"],
  ["Dagens pass", "today's session"],
  ["Snabbkoll", "quick check"],
  ["Uppgift (som på tentan)", "exam-style problem"],
  ["säkerhet", "confidence"],
  ["ledtråd", "hint"],
  ["bedömningspunkter", "rubric points"],
  ["begreppskort", "flashcard"],
  ["övningstenta, formelsamling", "practice exam, formula sheet"],
  ["kinetisk energi, rörelsemängd", "kinetic energy, momentum"],
  ["tidsdilatation, längdkontraktion, egentid", "time dilation, length contraction, proper time"],
  ["svartkroppsstrålning", "blackbody radiation"],
  ["bromsspänning", "stopping potential"],
  ["utträdesarbete", "work function"],
  ["tröskelfrekvens, tröskelvåglängd", "threshold frequency, threshold wavelength"],
  ["Comptonskift, Comptonformeln", "Compton shift, the Compton formula"],
  ["våg-partikeldualitet", "wave–particle duality"],
  ["osäkerhetsrelationen", "the uncertainty principle"],
  ["spinn upp, spinn ned", "spin up, spin down"],
  ["bindningsenergi", "binding energy"],
  ["halveringstid, sönderfallskonstant", "half-life, decay constant"],
  ["halvvärdestjocklek (HVL)", "half-value layer (HVL)"],
];

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card
      as="section"
      id={id}
      aria-labelledby={`${id}-title`}
      title={<span id={`${id}-title`}>{title}</span>}
    >
      {children}
    </Card>
  );
}

export function HelpPage() {
  return (
    <>
      <PageHeader
        title="Så fungerar sidan"
        description="Orden sidan använder, hur du skriver svar och kortkommandona."
      />
      <nav aria-label="Innehåll" className="mb-6 text-sm">
        <ul className="flex flex-wrap gap-x-5 gap-y-1">
          <li>
            <a className={LINK} href="#ord">
              Ord du kommer att se
            </a>
          </li>
          <li>
            <a className={LINK} href="#svar">
              Så skriver du svar
            </a>
          </li>
          <li>
            <a className={LINK} href="#tangenter">
              Kortkommandon
            </a>
          </li>
          <li>
            <a className={LINK} href="#english">
              English glossary
            </a>
          </li>
        </ul>
      </nav>

      <div className="flex flex-col gap-6">
        <Section id="ord" title="Ord du kommer att se">
          <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-[minmax(10rem,auto)_1fr]">
            {WORDS.map(([term, text]) => (
              <div key={term} className="contents">
                <dt className="font-medium text-ink-900 dark:text-ink-50">{term}</dt>
                <dd className="text-ink-700 dark:text-ink-300">{text}</dd>
              </div>
            ))}
          </dl>
        </Section>

        <Section id="svar" title="Så skriver du svar">
          <p className="mb-3 text-sm text-ink-700 dark:text-ink-300">
            Raden under svarsrutan visar hur ditt svar tolkas innan du skickar det. Står det att
            svaret inte går att läsa, ändra skrivsättet.
          </p>
          <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-[minmax(12rem,auto)_1fr]">
            {ANSWER_EXAMPLES.map(([example, text]) => (
              <div key={example} className="contents">
                <dt>
                  <code className="rounded bg-ink-100 px-1.5 py-0.5 text-ink-900 dark:bg-ink-800 dark:text-ink-50">
                    {example}
                  </code>
                </dt>
                <dd className="text-ink-700 dark:text-ink-300">{text}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-3 text-sm text-ink-700 dark:text-ink-300">
            På telefonen finns knappar för minus, ×10^, e och komma under rutan.
          </p>
        </Section>

        <Section id="tangenter" title="Kortkommandon">
          <p className="mb-3 text-sm text-ink-700 dark:text-ink-300">
            Bokstäver och siffror fungerar när du inte skriver i en textruta.
          </p>
          <ul className="flex flex-col gap-2 text-sm">
            {KEYS.map(({ id, keys, text }) => (
              <li key={id} className="flex flex-wrap items-baseline gap-2">
                <span className="flex gap-1">
                  {keys.map((key) => (
                    <Kbd key={key}>{key}</Kbd>
                  ))}
                </span>
                <span className="text-ink-700 dark:text-ink-300">{text}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section id="english" title="English glossary">
          <div lang="en">
            <p className="mb-3 text-sm text-ink-700 dark:text-ink-300">
              The site is in Swedish. The Y&amp;F references are to the English edition of the book
              (Young &amp; Freedman, University Physics, 15th ed.). Here are the words you will meet
              most often.
            </p>
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-ink-200 dark:border-ink-800">
                  <th scope="col" className="py-1.5 pr-4 font-medium">
                    Swedish
                  </th>
                  <th scope="col" className="py-1.5 font-medium">
                    English
                  </th>
                </tr>
              </thead>
              <tbody>
                {ENGLISH.map(([sv, en]) => (
                  <tr key={sv} className="border-b border-ink-100 dark:border-ink-900">
                    <td lang="sv" className="py-1.5 pr-4 text-ink-900 dark:text-ink-50">
                      {sv}
                    </td>
                    <td className="py-1.5 text-ink-700 dark:text-ink-300">{en}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <p className="text-sm text-ink-600 dark:text-ink-400">
          <Link className={LINK} to={REOPEN_HREF}>
            Svara på Kom igång-frågorna igen
          </Link>
          {" · "}
          <Link className={LINK} to="/integritet">
            Vad som sparas och vad läraren ser
          </Link>
        </p>
      </div>
    </>
  );
}

export default HelpPage;
